// Analysis preset route (overview, dailyAnalysis, trendAnalysis, ...)
// react
import { Suspense, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

// internationalization
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useParams } from '@tanstack/react-router';

// material ui
import { Box, CircularProgress, Typography } from '@mui/material';

// api — generated
import type { AnalysisType } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';
// api — generated
import { useGetLinkedEnergyGroupsLinkedEnergyGroupsAccountIdGetQuery } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMeters';

// shared components
import PageContainer from '@/components/layout/PageContainer';

// components
import PortfolioAnalysisDataGrid from '@/features/energyxplorer/portfolio/components/datagrid/type-datagrid/PortfolioAnalysisDataGrid';
import DataIntegrityDialog from '@/features/energyxplorer/portfolio/components/meter-drawer/data-integrity/DataIntegrityDialog';
import MeterDrawer from '@/features/energyxplorer/portfolio/components/meter-drawer/MeterDrawer';
import useMatrikelList from '@/features/energyxplorer/portfolio/hooks/useMatrikelList';
import { presetKeys } from '@/features/energyxplorer/portfolio/utils/pa-presets';
import type { EnrichedMeter } from '@/features/energyxplorer/types';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

export const Route = createFileRoute(
  '/$accountId/_user/energyxplorer/portfolio/$analysisType/analysis/$preset',
)({
  component: RouteComponent,
  staticData: {
    crumb: ({ params }) => t(`glossary:${params.preset}`),
  },
});

function RouteComponent() {
  const { t } = useTranslation('portfolio-analysis');
  const account = useSelector(getSelectedAccount);

  const { analysisType, preset: presetParam } = useParams({ strict: false });
  const preset = Math.max(presetKeys.indexOf(presetParam), 0);

  const {
    matrikel_list,
    isLoading,
    isFetching,
    powerSummedData,
    heatSummedData,
    waterSummedData,
  } = useMatrikelList();
  const summedData =
    analysisType === 'power'
      ? powerSummedData
      : analysisType === 'heat'
        ? heatSummedData
        : waterSummedData;
  const { data: linkedEnergyGroupsData } =
    useGetLinkedEnergyGroupsLinkedEnergyGroupsAccountIdGetQuery(
      { accountId: account?.AccountId },
      { skip: !account?.AccountId },
    );

  const enrichedData = useMemo(() => {
    if (!matrikel_list?.length) return null;

    const linkedEnergyGroups = linkedEnergyGroupsData?.LinkedEnergyGroups ?? [];

    const portfolio_analysis_list = matrikel_list
      .filter((item) => {
        const typeData = item[analysisType as AnalysisType];
        return (
          (typeData?.meter_list?.length ?? 0) > 0 ||
          Object.keys(typeData?.matrikel_analysis ?? {}).length > 0
        );
      })
      .map((item) => {
        const { metadata } = item;
        const typeData = item[analysisType as AnalysisType];
        const matrikelAnalysis =
          (typeData?.matrikel_analysis as Record<string, unknown>) ?? {};

        // out of sync if any meter for this energy type is not synced
        const meter_in_sync =
          typeData?.meter_list?.every(
            (entry) => entry.metadata.meter_in_sync === true,
          ) ?? true;

        return {
          ...matrikelAnalysis,
          matrikel_key: metadata.MatrikelKey,
          display_name: metadata.Address || metadata.MainAddress,
          type: 'matrikel',
          meter_in_sync,
          ExtractedBuildingUse: metadata.OverwriteFlag
            ? metadata.OverwriteExtractedBuildingUse ||
              metadata.ExtractedBuildingUse
            : metadata.ExtractedBuildingUse,
          linked_energy_groups: linkedEnergyGroups
            .find((leg) => leg.MatrikelKey === metadata.MatrikelKey)
            ?.LinkedEnergyGroups.filter(
              (group) => !group.type?.toLowerCase().includes('portfolio'),
            ),
        };
      });

    return { portfolio_analysis_list, summed_data: summedData ?? [] };
  }, [matrikel_list, analysisType, linkedEnergyGroupsData, summedData]);
  console.log(
    'enrichedData',
    enrichedData,
    matrikel_list,
    linkedEnergyGroupsData,
  );
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState<Record<string, unknown>>();

  const handleSetSelectedMeter = (meter) => {
    setIsOpen(true);
    setSelectedMeter(meter);
  };

  const [meterType, setMeterType] = useState('');

  return (
    <PageContainer>
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '80vh',
            verticalAlign: 'center',
          }}
        >
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 4 }}>
            {t('pages.loading_analysis')}
          </Typography>
        </Box>
      ) : (
        <>
          {enrichedData && (
            <Suspense
              fallback={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: '80vh',
                    verticalAlign: 'center',
                  }}
                >
                  <CircularProgress />
                </Box>
              }
            >
              <Box
                maxHeight={'80vh'}
                display={'flex'}
                flexDirection={'column'}
                sx={{ mt: 0, pt: 0 }}
              >
                <PortfolioAnalysisDataGrid
                  key={analysisType}
                  data={enrichedData}
                  matrikelList={matrikel_list}
                  setSelectedMeter={handleSetSelectedMeter}
                  setMeterType={setMeterType}
                  analysisType={analysisType}
                  isLoading={isFetching}
                  chosenPreset={preset}
                />
              </Box>
            </Suspense>
          )}
        </>
      )}
      {selectedMeter && (
        <Suspense
          fallback={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <MeterDrawer
            key={String(
              analysisType === 'power'
                ? selectedMeter.metering_point_id
                : ((
                    selectedMeter.data_stream as
                      | Record<string, unknown>
                      | undefined
                  )?.unit_id ?? selectedMeter?.matrikel_key),
            )}
            open={isOpen}
            onClose={() => {
              setIsOpen(false);
              setSelectedMeter(undefined);
            }}
            meter={selectedMeter as EnrichedMeter & Record<string, unknown>}
            meterType={meterType as 'meter' | 'matrikel'}
            analysisType={analysisType as AnalysisType}
          />
        </Suspense>
      )}
      <DataIntegrityDialog />
    </PageContainer>
  );
}
